<script setup>
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlModal from '../xl/XlModal.vue';
import XlTooltip from '../xl/XlTooltip.vue';
import useResourceDownload, { downloadFileName } from '../../composables/useResourceDownload';
import { relativeDay } from '../../helpers/relativeDay';
import { isSoleRecipient, othersOn, removeMemberResource } from '../resource-access/memberResourceRemoval';
import { isBroker } from '../../helpers/roles';
import { useAuthStore } from '../../stores/auth';

/**
 * The prototype's "Resources just for them" card: files only this person can
 * see, previewed from here, renamed and removed in the card's own edit mode.
 */
const props = defineProps({
  // { id: local users.id, first_name, label }
  member: { type: Object, required: true },
  canManage: { type: Boolean, default: false },
});

const emit = defineEmits(['upload-requested']);

const { open: openResource } = useResourceDownload();
const authStore = useAuthStore();

// A broker may only change what they made; the agency's file for their
// policyholder stays on the card with nothing to press that would only ever
// refuse. Owners and staff change any row.
const mine = (resource) => !isBroker(authStore.userData) || resource.created_by === authStore.userData?.subdomain_user_id;

const api = () => window.store.event_backend_api;

const resources = ref([]);
const loading = ref(true);
const editing = ref(false);
const names = ref({});
const saving = ref(false);
const pendingRemoval = ref(null);
const removing = ref(false);

const load = async () => {
  try {
    const { data } = await axios.get(`${api()}/resources`, {
      params: { scope: 'mine', member_id: props.member.id, per_page: 100 },
    });
    resources.value = data?.data ?? [];
  } catch {
    resources.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(load);
defineExpose({ reload: load });

const firstName = computed(() => props.member.first_name || 'them');

const startEdit = () => {
  names.value = Object.fromEntries(resources.value.filter(mine).map((resource) => [resource.id, resource.name ?? '']));
  editing.value = true;
};

// Only rows the caller may change, whatever the edit field holds: the
// viewer's scope can change under an open edit, and a rename the API would
// refuse is not worth sending.
const renamed = computed(() => resources.value.filter((resource) => {
  if (!mine(resource)) return false;
  const name = (names.value[resource.id] ?? '').trim();
  return name && name !== resource.name;
}));

const done = async () => {
  if (!renamed.value.length) {
    editing.value = false;
    return;
  }

  saving.value = true;
  try {
    for (const resource of renamed.value) {
      await axios.patch(`${api()}/media-items/${resource.id}`, { name: names.value[resource.id].trim() });
    }
    await load();
    editing.value = false;
    window.loadNotification?.({ message: 'Saved' });
  } catch (error) {
    await load();
    window.loadNotification?.({ message: error?.response?.data?.message || 'Could not rename that file', error: true });
  } finally {
    saving.value = false;
  }
};

const preview = async (resource) => {
  try {
    await openResource(resource);
  } catch {
    window.loadNotification?.({ message: 'Could not open that file', error: true });
  }
};

const removalMessage = computed(() => {
  const resource = pendingRemoval.value;
  if (!resource) return '';

  const others = othersOn(resource, props.member.id).length;

  return others
    ? `${props.member.label} loses it now. It stays with ${others} other ${others === 1 ? 'person' : 'people'}.`
    : `${props.member.label} loses it now, and the file is deleted.`;
});

const confirmRemoval = async () => {
  const resource = pendingRemoval.value;
  if (!resource || removing.value) return;

  removing.value = true;
  try {
    await removeMemberResource(resource, props.member.id);
    pendingRemoval.value = null;
    await load();
  } catch {
    window.loadNotification?.({ message: 'Could not remove that file', error: true });
  } finally {
    removing.value = false;
  }
};
</script>

<template>
  <XlCard padded class="resources" data-testid="record-resources">
    <div class="resources__head">
      <h2>
        <XlTooltip text="Files shared privately with this person — nobody else in the portal can see them." underline>Resources</XlTooltip>
        just for them
      </h2>
      <span v-if="props.canManage" class="resources__actions">
        <XlButton variant="ghost" size="sm" data-testid="resources-add" @click="emit('upload-requested')">
          <template #icon>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 6.2v11.6M6.2 12h11.6" />
            </svg>
          </template>
          Add
        </XlButton>
        <XlButton
          v-if="resources.length"
          :variant="editing ? 'filled' : 'ghost'"
          size="sm"
          :loading="saving"
          data-testid="resources-edit"
          @click="editing ? done() : startEdit()"
        >{{ editing ? 'Done' : 'Edit' }}</XlButton>
      </span>
    </div>

    <p v-if="loading" class="resources__empty">Loading…</p>

    <p v-else-if="!resources.length" class="resources__empty" data-testid="resources-empty">
      Nothing yet. Anything added here is visible only to {{ firstName }} — useful for a renewal quote or a survey.
    </p>

    <template v-else>
      <div class="resources__list">
      <div v-for="resource in resources" :key="resource.id" class="resources__row" data-testid="resource-row">
        <span class="resources__icon">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M13.6 4H7.5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V9z" />
            <path d="M13.6 4v5h4.9" />
          </svg>
        </span>

        <input
          v-if="editing && mine(resource)"
          v-model="names[resource.id]"
          class="resources__name-input"
          maxlength="150"
          :aria-label="`Name of ${resource.name}`"
          data-testid="resource-name"
        />
        <span v-else class="resources__what">
          <b>{{ downloadFileName(resource) }}</b>
          <span>Added {{ relativeDay(resource.created_at) }} · private to {{ firstName }}</span>
        </span>

        <XlButton variant="ghost" size="sm" data-testid="resource-preview" @click="preview(resource)">Preview</XlButton>

        <button
          v-if="editing && mine(resource)"
          type="button"
          class="resources__remove"
          :aria-label="`Remove ${resource.name}`"
          data-testid="resource-remove"
          @click="pendingRemoval = resource"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true">
            <path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8" />
          </svg>
        </button>
      </div>
      </div>

      <p class="resources__note">PDF, DOC, JPG or PNG · max 20 MB each</p>
    </template>

    <XlModal
      v-if="pendingRemoval"
      :title="isSoleRecipient(pendingRemoval, props.member.id) ? 'Delete this file?' : 'Take this file away from them?'"
      max-width="440px"
      @close="pendingRemoval = null"
    >
      <p class="resources__confirm" data-testid="resource-remove-message">{{ removalMessage }}</p>
      <template #actions>
        <XlButton variant="ghost" size="sm" @click="pendingRemoval = null">Keep it</XlButton>
        <XlButton variant="danger" size="sm" :loading="removing" data-testid="resource-remove-confirm" @click="confirmRemoval">
          {{ isSoleRecipient(pendingRemoval, props.member.id) ? 'Delete' : 'Remove' }}
        </XlButton>
      </template>
    </XlModal>
  </XlCard>
</template>

<style scoped>
.resources, .resources * { box-sizing: border-box; }

.resources__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.resources__head h2 {
  flex: 0 1 auto;
  margin: 0 10px 0 0;
  font-size: 15px;
  line-height: normal;
  font-weight: 700;
}

.resources__actions { display: flex; gap: 10px; margin-left: auto; }

.resources__empty {
  margin: 8px 0 0;
  font-size: 13.5px;
  color: var(--xl-color-outline);
}

/* The prototype's `.row`. */
.resources__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 11px 0;
  border-bottom: 1px solid var(--xl-color-border-subtle);
}

.resources__row:last-child { border-bottom: 0; }

.resources__icon {
  display: grid;
  place-items: center;
  flex: 0 0 42px;
  width: 42px;
  height: 34px;
  border-radius: 13px;
  background: var(--xl-color-primary-container);
  color: var(--xl-color-primary);
}

.resources__what { flex: 1; min-width: 0; }

.resources__what b {
  display: block;
  font-size: 14px;
  overflow-wrap: anywhere;
}

.resources__what span {
  font-size: 13px;
  color: var(--xl-color-outline);
}

.resources__name-input {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--xl-color-border);
  border-radius: 11px;
  background: #fff;
  font: inherit;
  font-size: 14.5px;
  color: var(--xl-color-primary-text);
}

.resources__name-input:focus {
  outline: none;
  border-color: var(--xl-color-primary);
  box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1);
}

.resources__remove {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--xl-color-border);
  border-radius: 11px;
  background: transparent;
  color: var(--xl-color-error);
  cursor: pointer;
}

.resources__note {
  margin: 12px 0 0;
  font-size: 12px;
  color: var(--xl-color-outline);
}

.resources__confirm { margin: 0; font-size: 14px; }
</style>
